/**
 * View-model для HomePage.
 *
 * Данные вручную extracted из `content/*.md`. Markdown-пайплайн в Gen 2
 * отдаёт только `html` / `rawMarkdown`, structured-схемы (solutions[],
 * modules[], workflow[], cases[]) ещё не материализованы для главной —
 * их будет переносить Gen 6 для калькулятора/брифа.
 *
 * Пока что хранить карточные списки в виде типизированных TS-литералов
 * здесь — единственный способ отдать домен-компонентам реальный
 * контент, не парся markdown в рантайме и не плодя i18n-ключи на
 * каждую карточку.
 *
 * Источники (все внутри `/content/`):
 *   - 00-positioning.md        — hero, stats, патент-цитата
 *   - 01-platform.md           — platform trio (4 элемента)
 *   - 02-ready-solutions.md    — 14 готовых решений
 *   - 03-engine-modules.md     — 14 модулей PromoKit + 1 служебный
 *   - 04-visual-builder.md     — конструктор (блоки)
 *   - 05-integrations-deployment.md — 2 сценария развёртывания + интеграции
 *   - 06-workflow.md           — 6 этапов
 *   - 07-cases.md              — featured + 13 кейсов
 *
 * НИКАКОЙ копипасты копирайта — только структурированные данные.
 * Если текст расходится с markdown, правим этот файл под markdown.
 *
 * i18n: RU — базовое поле (`title`, `description`, …). EN-перевод
 * живёт на параллельном `*En`-поле (`titleEn`, `descriptionEn`, …),
 * компоненты выбирают значение через `pickLocalized`.
 */

import type { IconName } from '@/components/base/icons';

export interface StatItem {
  readonly value: string;
  readonly label: string;
  readonly labelEn?: string;
  readonly helper?: string;
  readonly helperEn?: string;
}

export interface PlatformItem {
  readonly id: string;
  readonly iconName: string;
  readonly title: string;
  readonly titleEn?: string;
  readonly description: string;
  readonly descriptionEn?: string;
  readonly anchor: string;
}

export interface SolutionItem {
  readonly id: string;
  readonly title: string;
  readonly titleEn?: string;
  readonly description: string;
  readonly descriptionEn?: string;
  readonly details?: readonly string[];
  readonly detailsEn?: readonly string[];
  /**
   * Slug подстраницы из `@/content/subproducts.ts`. Если задан —
   * карточка превращается в роутер-ссылку `/:lang/solutions/:subproductId`.
   * Если не задан — поведение карточки прежнее (CTA эмитит `open`).
   */
  readonly subproductId?: string;
}

export interface ModuleItem {
  readonly id: string;
  readonly title: string;
  readonly titleEn?: string;
  readonly description: string;
  readonly descriptionEn?: string;
  readonly status: 'ready' | 'beta' | 'planned';
  readonly icon?: IconName;
}

export interface BuilderBlockItem {
  readonly id: string;
  readonly title: string;
  readonly titleEn?: string;
}

export interface IntegrationItem {
  readonly id: string;
  readonly name: string;
  readonly nameEn?: string;
  readonly protocol: string;
  readonly protocolEn?: string;
  readonly icon: IconName;
}

export interface DeploymentItem {
  readonly id: 'smb' | 'enterprise';
  readonly title: string;
  readonly titleEn?: string;
  readonly description: string;
  readonly descriptionEn?: string;
  readonly bullets: readonly string[];
  readonly bulletsEn?: readonly string[];
}

export interface WorkflowStepItem {
  readonly id: string;
  readonly index: string;
  readonly title: string;
  readonly titleEn?: string;
  readonly subtitle: string;
  readonly subtitleEn?: string;
  readonly receives: readonly string[];
  readonly receivesEn?: readonly string[];
  readonly cta?: string;
  readonly ctaEn?: string;
}

/**
 * Какой цвет «чернил» использовать для текста на брендовом фоне карточки.
 *
 * - `inverse` — белый текст (`text-ink-inverse`); подходит для тёмных
 *   и насыщенных брендов (бордо, изумруд, charcoal).
 * - `strong` — тёмный текст (`text-ink-strong`); обязателен для светлых
 *   брендовых фонов (lime, sky-blue, mint, оранжевый), где белый
 *   проваливает контраст по WCAG AA.
 *
 * Решение про каждый кейс принимается вручную при заведении в `CASES`
 * — ни алгоритмический luminance-чекер, ни runtime-фолбэк не нужны:
 * редактор данных видит постер и выбирает осознанно.
 */
export type CaseAccentInk = 'inverse' | 'strong';

/**
 * Карточка кейса в формате «постер»: брендовый фон, лого, eyebrow,
 * заголовок проекта, короткое описание и сам постер из `public/cases/`.
 *
 * Поле `image` — абсолютный путь от корня публичной папки (картинки
 * не идут через Vite-импорт, потому что лежат в `public/`).
 */
export interface CaseItem {
  readonly id: string;
  readonly client: string;
  readonly clientEn: string;
  readonly eyebrow: string;
  readonly eyebrowEn: string;
  readonly title: string;
  readonly titleEn: string;
  readonly description: string;
  readonly descriptionEn: string;
  readonly image: string;
  readonly imageAlt: string;
  readonly imageAltEn: string;
  /** Доминирующий брендовый цвет постера (HEX). Используется как фон карточки. */
  readonly accent: string;
  readonly accentInk: CaseAccentInk;
}

/**
 * Композиционный шаблон промо-акции. В отличие от `SolutionItem`
 * (перечисление готовых решений) и `ModuleItem` (технический модуль
 * движка), `PromoTemplate` — это «комплект» из механики + интеграций
 * + UX, готовый к запуску: пользователь выбирает его и запрашивает
 * старт. Карточки подчёркивают distinguishing traits («что внутри»).
 */
export interface PromoTemplate {
  readonly id: string;
  readonly icon: IconName;
  readonly title: string;
  readonly titleEn: string;
  readonly description: string;
  readonly descriptionEn: string;
  /** 3–4 distinguishing traits, short phrases. */
  readonly traits: readonly string[];
  readonly traitsEn: readonly string[];
  /** Estimated launch window in weeks, e.g. "2–3 недели". */
  readonly timeline: string;
  readonly timelineEn: string;
}

export interface LogoItem {
  readonly name: string;
  readonly src?: string;
  readonly shellBg?: string;
}

/* -------------------------------------------------------------------------- */
/* Hero / stats                                                               */
/* -------------------------------------------------------------------------- */

export const HERO_STATS: readonly StatItem[] = [
  { value: '8', label: 'лет на рынке', labelEn: 'years in business' },
  { value: '900+', label: 'промо-сайтов', labelEn: 'promo sites' },
  { value: '249+', label: 'клиентов', labelEn: 'clients' },
];

export const TRUST_STATS: readonly StatItem[] = [
  {
    value: '420+',
    label: 'акций со сложной механикой',
    labelEn: 'promos with complex mechanics',
  },
  { value: '60%', label: 'экономия времени', labelEn: 'time saved' },
  { value: '63', label: 'пункта чек-листа качества', labelEn: 'quality-checklist points' },
  { value: '7+', label: 'лет опыт разработчиков', labelEn: 'years of developer experience' },
];

export const HERO_BULLETS: readonly string[] = [
  '14 инструментов для запуска',
  'Команда профессионалов работает над вашим проектом',
  'Ваш личный менеджер 24 часа в сутки',
];

export const HERO_BULLETS_EN: readonly string[] = [
  '14 tools to launch with',
  'A seasoned team assigned to your project',
  'A dedicated manager on call 24/7',
];

/**
 * Список логотипов для Trust/Proof-секции.
 *
 * Источник ассетов: `site/public/logos/new_clients` → нормализованные файлы в
 * `/logos/clients/`. Оставшиеся `Партнёр N` — импорты без однозначного файла
 * (trust-import / Photoroom); подписать фактическими брендами после подтверждения
 * у дизайна. Логотипы без ссылок — декоративные, не кликабельные.
 * `shellBg` — опциональный фон обёртки (EPICA, Mealberry).
 *
 * TRADEMARK: названия и изображения — товарные знаки правообладателей;
 * перед публичным продакшеном заменить на ассеты из press-kit'ов с разрешением.
 */
export const TRUST_LOGOS: readonly LogoItem[] = [
  { name: 'Love is', src: '/logos/clients/love_is.svg' },
  { name: 'Heineken', src: '/logos/clients/heineken.svg' },
  { name: 'Chernogolovka', src: '/logos/clients/chernogolovka.svg' },
  { name: 'Brewdog', src: '/logos/clients/brewdog.svg' },
  { name: 'Ehrmann', src: '/logos/clients/ehrmann.svg' },
  { name: 'Zetter', src: '/logos/clients/zetter.svg' },
  { name: 'Milosladovo', src: '/logos/clients/milosladovo.svg' },
  { name: 'Birra Moretti', src: '/logos/clients/birra-moretti.svg' },
  { name: 'Jan Sobeski', src: '/logos/clients/jan_sobeski.svg' },
  { name: 'Mealberry', src: '/logos/clients/mealberry.svg', shellBg: '#b80f63' },
  { name: 'President', src: '/logos/clients/president.svg' },
  { name: 'Epica', src: '/logos/clients/epica.svg', shellBg: '#061e41' },
  { name: 'Lubytovo', src: '/logos/clients/lubytovo.svg' },
  { name: 'United breweries', src: '/logos/clients/united_breweries.svg' },
  { name: 'Hochland', src: '/logos/clients/hochland.svg' },
  { name: 'Rusking', src: '/logos/clients/rusking.svg' },
  { name: 'Keaz', src: '/logos/clients/keaz.svg' },
];

/* -------------------------------------------------------------------------- */
/* 4 элемента платформы (content/01-platform.md)                              */
/* -------------------------------------------------------------------------- */

export const PLATFORM_TRIO: readonly PlatformItem[] = [
  {
    id: 'solutions',
    iconName: 'check',
    title: '14 пакетов готовых решений',
    titleEn: '14 ready-made solutions',
    description: 'Промо-акция чековая, кодовая, конкурсная и т. д.',
    descriptionEn: 'Receipt, code, contest mechanics and more.',
    anchor: '#solutions',
  },
  {
    id: 'engine',
    iconName: 'success',
    title: 'Встроенный движок с 14 модулями',
    titleEn: 'Built-in engine with 14 modules',
    description:
      'Валидация чеков (ФНС), розыгрыши, обработка обратной связи, аналитика — модульно.',
    descriptionEn:
      'Receipt validation (FNS), prize draws, support desk, analytics — turn on what you need.',
    anchor: '#engine',
  },
  {
    id: 'builder',
    iconName: 'play',
    title: 'Визуальный zero-code конструктор',
    titleEn: 'Zero-code visual builder',
    description: 'Исключает или минимизирует участие разработчиков при создании сайта.',
    descriptionEn: 'Cuts developer involvement to a minimum when assembling the site.',
    anchor: '#builder',
  },
  {
    id: 'integrations',
    iconName: 'external',
    title: 'Интеграция с IT-сервисами компании',
    titleEn: 'Integrations with your IT stack',
    description: 'Интеграция по API с CRM, ERP и другими системами — внешними и внутренними.',
    descriptionEn: 'API integrations with CRM, ERP and other systems — internal or external.',
    anchor: '#integrations',
  },
];

/* -------------------------------------------------------------------------- */
/* 14 готовых решений (content/02-ready-solutions.md)                         */
/* -------------------------------------------------------------------------- */

export const READY_SOLUTIONS: readonly SolutionItem[] = [
  {
    id: 'landing',
    title: 'Лендинг',
    titleEn: 'Landing page',
    description: 'Быстрый запуск одностраничных лендингов.',
    descriptionEn: 'Launch a single-page landing fast.',
    subproductId: 'landing',
  },
  {
    id: 'promo-code',
    title: 'Промо-акция с кодовой механикой',
    titleEn: 'Code-based promo',
    description: 'Запуск кодовых промо «под ключ» с готовыми модулями.',
    descriptionEn: 'Turnkey code-based promos with ready-made modules.',
    details: ['Регистрация и авторизация', 'Загрузка кодов', 'Публикация победителей'],
    detailsEn: ['Registration and sign-in', 'Code upload', 'Winners announcement'],
    subproductId: 'promo-code',
  },
  {
    id: 'promo-receipt',
    title: 'Промо-акция с чековой механикой',
    titleEn: 'Receipt-based promo',
    description: 'Чековые акции с валидацией через ФНС и личным кабинетом.',
    descriptionEn: 'Receipt promos with FNS validation and a participant dashboard.',
    details: ['Регистрация', 'Загрузка чеков', 'Запрос призов'],
    detailsEn: ['Registration', 'Receipt upload', 'Prize claims'],
    subproductId: 'promo-receipt',
  },
  {
    id: 'promo-contest',
    title: 'Промо-акция с конкурсной механикой',
    titleEn: 'Contest-based promo',
    description: 'Фото- и видео-конкурсы с голосованием и модерацией.',
    descriptionEn: 'Photo and video contests with voting and moderation.',
    subproductId: 'promo-contest',
  },
  {
    id: 'chatbot',
    title: 'Чат-бот',
    titleEn: 'Chatbot',
    description: 'Боты в мессенджерах для частичной или полной механики.',
    descriptionEn: 'Messenger bots covering part or all of the mechanic.',
    subproductId: 'chatbot',
  },
  {
    id: 'site',
    title: 'Сайт',
    titleEn: 'Website',
    description: 'Шаблоны и блоки для полноценного многостраничного сайта.',
    descriptionEn: 'Templates and blocks for a full multi-page site.',
    subproductId: 'site',
  },
  {
    id: 'btl',
    title: 'BTL / Приложение промоутера',
    titleEn: 'BTL / promoter app',
    description: 'Приложение для смартфонов с офлайн-режимом и мониторингом.',
    descriptionEn: 'A smartphone app with offline mode and live monitoring.',
  },
  {
    id: 'conlan',
    title: 'Web чат-бот / ConLan',
    titleEn: 'Web chatbot / ConLan',
    description: 'Conversational landing — диалог через готовые виджеты и кнопки.',
    descriptionEn: 'Conversational landing — a dialog driven by prebuilt widgets and buttons.',
  },
  {
    id: 'corporate',
    title: 'Корпоративные сервисы',
    titleEn: 'Corporate services',
    description: 'Формы опросов, квест-механики для мероприятий и выставок.',
    descriptionEn: 'Survey forms and quest mechanics for events and trade shows.',
  },
  {
    id: 'quiz',
    title: 'Квиз / Викторина',
    titleEn: 'Quiz',
    description: 'Квизы с этапностью, сегментацией и учётом часовых поясов.',
    descriptionEn: 'Quizzes with staged flows, segmentation and timezone handling.',
  },
  {
    id: 'games',
    title: 'Игры',
    titleEn: 'Games',
    description: 'Шаблоны игровых механик для брендированных игр.',
    descriptionEn: 'Game-mechanic templates for branded mini-games.',
  },
  {
    id: 'interactive',
    title: 'Интерактивный контент',
    titleEn: 'Interactive content',
    description: 'Микроприложения с развлекательным или обучающим контентом.',
    descriptionEn: 'Micro-apps with entertainment or training content.',
  },
  {
    id: 'digital-mockup',
    title: 'Цифровой макет (брендбук)',
    titleEn: 'Digital mock-up (brand book)',
    description: 'Печатные и веб-макеты из шаблонов, PDF на выходе.',
    descriptionEn: 'Print and web mock-ups from templates, PDF output.',
  },
  {
    id: 'qr',
    title: 'Генератор QR-кодов',
    titleEn: 'QR code generator',
    description: 'QR с айдентикой бренда — офлайн-канал доступа к сервисам.',
    descriptionEn: 'Branded QR codes — an offline channel into your services.',
  },
];

/* -------------------------------------------------------------------------- */
/* 14 модулей PromoKit + 1 служебный (content/03-engine-modules.md)           */
/* -------------------------------------------------------------------------- */

export const ENGINE_MODULES: readonly ModuleItem[] = [
  {
    id: 'crm',
    title: 'Модуль CRM',
    titleEn: 'CRM module',
    description: 'Хранит персональные данные, чеки, тикеты и призы участников.',
    descriptionEn: 'Stores personal data, receipts, tickets and prizes for participants.',
    status: 'ready',
    icon: 'users',
  },
  {
    id: 'receipts',
    title: 'Обработка чеков и кодов',
    titleEn: 'Receipt and code processing',
    description: 'Распознаёт чеки, валидирует через ФНС, идентифицирует торговые точки.',
    descriptionEn: 'Reads receipts, validates them through FNS and identifies retail outlets.',
    status: 'ready',
    icon: 'receipt',
  },
  {
    id: 'prizes',
    title: 'Призы и розыгрыши',
    titleEn: 'Prizes and draws',
    description: 'Призовой фонд, автоматические и ручные розыгрыши.',
    descriptionEn: 'Prize pool with automatic and manual draws.',
    status: 'ready',
    icon: 'gift',
  },
  {
    id: 'feedback',
    title: 'Обработка обратной связи',
    titleEn: 'Feedback handling',
    description: 'Интерфейс тикетов от участников промо.',
    descriptionEn: 'A ticketing inbox for participant messages.',
    status: 'ready',
    icon: 'message-circle',
  },
  {
    id: 'analytics',
    title: 'Аналитика',
    titleEn: 'Analytics',
    description: 'Онлайн-показатели кампании и выгрузки по кнопке.',
    descriptionEn: 'Live campaign metrics and one-click exports.',
    status: 'ready',
    icon: 'bar-chart-3',
  },
  {
    id: 'auth',
    title: 'Регистрация и авторизация',
    titleEn: 'Sign-up and sign-in',
    description: 'E-mail, SMS, соцсети, мессенджеры.',
    descriptionEn: 'Email, SMS, social networks, messengers.',
    status: 'ready',
    icon: 'user-plus',
  },
  {
    id: 'ym',
    title: 'Яндекс Метрика',
    titleEn: 'Yandex Metrica',
    description: 'Ключевые метрики и цели без перехода в интерфейс Яндекс Метрики.',
    descriptionEn: 'Key metrics and goals without leaving the Bafsy dashboard.',
    status: 'ready',
    icon: 'trending-up',
  },
  {
    id: 'receipts-messengers',
    title: 'Приём чеков через мессенджеры',
    titleEn: 'Receipt intake via messengers',
    description: 'Telegram / ВКонтакте / WhatsApp — регистрация и валидация чеков.',
    descriptionEn: 'Telegram, VKontakte, WhatsApp — receipt registration and validation.',
    status: 'ready',
    icon: 'send',
  },
  {
    id: 'payments',
    title: 'Платёжные системы',
    titleEn: 'Payment systems',
    description: 'Моментальные выплаты: Яндекс Деньги, Гифтери и др.',
    descriptionEn: 'Instant payouts: YooMoney, Giftery and more.',
    status: 'ready',
    icon: 'credit-card',
  },
  {
    id: 'winners',
    title: 'Публикация победителей',
    titleEn: 'Winners board',
    description: 'Автоподтягивание в раздел на сайте с фильтрами.',
    descriptionEn: 'Auto-published to a filterable section on the site.',
    status: 'ready',
    icon: 'trophy',
  },
  {
    id: 'codes',
    title: 'Генерация промо-кодов',
    titleEn: 'Promo code generation',
    description: 'От 1 до нескольких миллионов уникальных кодов.',
    descriptionEn: 'From a single code to several million unique ones.',
    status: 'ready',
    icon: 'ticket',
  },
  {
    id: 'referral',
    title: 'Реферальная система',
    titleEn: 'Referral system',
    description: 'Персональные ссылки и вознаграждения за целевое действие.',
    descriptionEn: 'Personal referral links and rewards for target actions.',
    status: 'ready',
    icon: 'share-2',
  },
  {
    id: 'share',
    title: 'Шеринг в соцсетях',
    titleEn: 'Social sharing',
    description: 'Публикация пользователем информации об акции с бонусом.',
    descriptionEn: 'Users share the promo and earn a bonus in return.',
    status: 'ready',
    icon: 'megaphone',
  },
  {
    id: 'games',
    title: 'Конкурсные и игровые механики',
    titleEn: 'Contest and game mechanics',
    description: 'Викторины, квизы, творческие конкурсы с подтягиванием постов.',
    descriptionEn: 'Quizzes and creative contests with social-post ingestion.',
    status: 'ready',
    icon: 'dices',
  },
  {
    id: 'reports',
    title: 'Выгрузка отчётов',
    titleEn: 'Report exports',
    description: 'Данные по пользователям, чекам, розыгрышам в Excel.',
    descriptionEn: 'Users, receipts and draws exported to Excel.',
    status: 'ready',
    icon: 'file-spreadsheet',
  },
];

/* -------------------------------------------------------------------------- */
/* Конструктор (content/04-visual-builder.md)                                 */
/* -------------------------------------------------------------------------- */

export const BUILDER_BLOCKS: readonly BuilderBlockItem[] = [
  { id: 'hero', title: 'Первый экран', titleEn: 'Hero screen' },
  { id: 'steps', title: 'Действия по шагам', titleEn: 'Step-by-step actions' },
  { id: 'winners', title: 'Победители', titleEn: 'Winners' },
  { id: 'faq', title: 'FAQ — частые вопросы', titleEn: 'FAQ' },
  { id: 'partners', title: 'Партнёры промо-акции', titleEn: 'Promo partners' },
  { id: 'popup', title: 'Pop-up регистрации и авторизации', titleEn: 'Sign-up / sign-in pop-up' },
  { id: 'header', title: 'Header — шапка с меню', titleEn: 'Header with menu' },
  { id: 'auth', title: 'Авторизация и регистрация', titleEn: 'Sign-in and sign-up' },
  { id: 'footer', title: 'Footer — подвал сайта', titleEn: 'Footer' },
];

export const BUILDER_PROMISES: readonly string[] = [
  'Сэкономьте 2 месяца на разработке и вёрстке',
  'Собирайте сайты из профильных шаблонов',
  'Запускайте промо без привлечения программистов',
];

export const BUILDER_PROMISES_EN: readonly string[] = [
  'Save two months on development and layout',
  'Assemble sites from purpose-built templates',
  'Launch promos without pulling in developers',
];

/* -------------------------------------------------------------------------- */
/* Интеграции и развёртывание (content/05-integrations-deployment.md)         */
/* -------------------------------------------------------------------------- */

export const DEPLOYMENT_SCENARIOS: readonly DeploymentItem[] = [
  {
    id: 'smb',
    title: 'Для малого и среднего бизнеса',
    titleEn: 'Small and mid-size business',
    description: 'Подключение через DNS или код. Данные на серверах Bafsy, шифрование включено.',
    descriptionEn:
      'Hook up via DNS or a code snippet. Data lives on Bafsy servers, encryption enabled.',
    bullets: ['Быстрый запуск', 'Облако Bafsy', 'Шифрование данных'],
    bulletsEn: ['Fast launch', 'Bafsy cloud', 'Encrypted data'],
  },
  {
    id: 'enterprise',
    title: 'Для корпорации',
    titleEn: 'Enterprise',
    description: 'On-premise: хранение и обработка данных на ваших серверах.',
    descriptionEn: 'On-premise: data stays on your servers, processed inside your perimeter.',
    bullets: ['Данные у заказчика', 'Интеграция с IT-контуром', 'Соответствие корп-политикам'],
    bulletsEn: [
      'Data on the client side',
      'Fits your IT perimeter',
      'Complies with corporate policies',
    ],
  },
];

export const INTEGRATIONS: readonly IntegrationItem[] = [
  { id: 'crm', name: 'CRM-системы', nameEn: 'CRM systems', protocol: 'REST / API', icon: 'users' },
  {
    id: 'erp',
    name: 'ERP-системы',
    nameEn: 'ERP systems',
    protocol: 'REST / API',
    icon: 'bar-chart-3',
  },
  {
    id: 'esb',
    name: 'Корпоративные шины данных',
    nameEn: 'Enterprise service buses',
    protocol: 'ESB',
    icon: 'share-2',
  },
  {
    id: 'payments',
    name: 'Платёжные системы',
    nameEn: 'Payment systems',
    protocol: 'REST',
    icon: 'credit-card',
  },
  {
    id: 'loyalty',
    name: 'Карты лояльности',
    nameEn: 'Loyalty cards',
    protocol: 'REST',
    icon: 'gift',
  },
  {
    id: 'ofd',
    name: 'ОФД',
    nameEn: 'OFD (Fiscal Data Operators)',
    protocol: 'готовые интеграции',
    protocolEn: 'prebuilt integrations',
    icon: 'receipt',
  },
  {
    id: 'fns',
    name: 'API ФНС',
    nameEn: 'FNS API',
    protocol: 'SOAP / REST',
    icon: 'file-spreadsheet',
  },
  {
    id: 'tg',
    name: 'Telegram Bot API',
    nameEn: 'Telegram',
    protocol: 'HTTPS',
    icon: 'send',
  },
  { id: 'vk', name: 'VK API', nameEn: 'VK', protocol: 'HTTPS', icon: 'message-circle' },
  //{ id: 'wa', name: 'WhatsApp Business', nameEn: 'WhatsApp', protocol: 'HTTPS', icon: 'message-circle' },
  { id: 'sms', name: 'SMS-шлюзы', nameEn: 'SMS gateways', protocol: 'SMPP', icon: 'smartphone' },
];

export const INTEGRATIONS_OUTCOMES: readonly string[] = [
  'Интеграция с IT-системой заказчика',
  'Доступ администратора к панели со статистикой и отчётами',
  'Модули, необходимые для реализации промо-кампании',
];

export const INTEGRATIONS_OUTCOMES_EN: readonly string[] = [
  "Integration with the client's IT stack",
  'Admin access to a dashboard with stats and reports',
  'The modules needed to run the promo campaign',
];

/* -------------------------------------------------------------------------- */
/* Workflow 6 этапов (content/06-workflow.md)                                 */
/* -------------------------------------------------------------------------- */

export const WORKFLOW_STEPS: readonly WorkflowStepItem[] = [
  {
    id: 'brief',
    index: '01',
    title: 'Сформируем ТЗ',
    titleEn: 'Draft the scope',
    subtitle: 'Собираем информацию и формируем техническое задание.',
    subtitleEn: 'We gather inputs and turn them into a written scope.',
    receives: [
      'Готовое ТЗ, соответствующее требованиям тендеров',
      'Смета проекта (оптимизация стоимости до 4-х раз)',
      'TimePlan + RACI + GANT',
      'Customer Journey под ваш проект',
    ],
    receivesEn: [
      'A scope ready to drop into tender paperwork',
      'A project estimate (up to 4× cost optimisation)',
      'TimePlan + RACI + Gantt',
      'A customer journey tailored to your project',
    ],
  },
  {
    id: 'design',
    index: '02',
    title: 'Создаём дизайн и верстаем сайт',
    titleEn: 'Design and build the site',
    subtitle: 'Создаём уникальный дизайн с учётом нюансов прототипа.',
    subtitleEn: "We craft a custom design built around the prototype's details.",
    receives: ['Прототип сайта', 'Уникальный дизайн'],
    receivesEn: ['A site prototype', 'A custom design'],
  },
  {
    id: 'builder',
    index: '03',
    title: 'Создадим проект на билдере Bafsy',
    titleEn: 'Assemble the project on the Bafsy builder',
    subtitle: 'Ускоряем разработку в 4 раза и оптимизируем стоимость.',
    subtitleEn: 'We build 4× faster and keep costs down.',
    receives: [
      'Экономия времени разработки до 60%',
      'Оптимизация стоимости до 4-х раз',
      'Высокий балл Google PageSpeed',
    ],
    receivesEn: [
      'Up to 60% less development time',
      'Up to 4× cost optimisation',
      'A high Google PageSpeed score',
    ],
  },
  {
    id: 'dev',
    index: '04',
    title: 'Выполним разработку с двойным контролем',
    titleEn: 'Build it with two levels of review',
    subtitle: 'Штатные разработчики с опытом 7+ лет.',
    subtitleEn: 'In-house developers with 7+ years of experience.',
    receives: ['Готовый промо-сайт, прошедший контроль качества (63 пункта)'],
    receivesEn: ['A promo site that has passed our 63-point quality review'],
  },
  {
    id: 'engine',
    index: '05',
    title: 'Подключим мощный движок',
    titleEn: 'Plug in the engine',
    subtitle: 'Оптимальный набор модулей за функциональную часть проекта.',
    subtitleEn: "The right set of modules to power the project's feature set.",
    receives: [
      'Интеграция с вашей IT-системой',
      'Модули, необходимые для реализации кампании',
      'Панель администратора со статистикой',
    ],
    receivesEn: [
      'Integration with your IT stack',
      'The modules needed to run the campaign',
      'An admin dashboard with live stats',
    ],
  },
  {
    id: 'support',
    index: '06',
    title: 'Гарантируем техническое обслуживание',
    titleEn: 'Run the site and back you up 24/7',
    subtitle: 'Круглосуточный мониторинг и оперативные изменения.',
    subtitleEn: 'Round-the-clock monitoring and fast updates.',
    receives: ['Выделенный менеджер', 'Мониторинг 24/7', 'Оперативные правки контента и модулей'],
    receivesEn: ['A dedicated manager', '24/7 monitoring', 'Fast content and module updates'],
  },
];

/* -------------------------------------------------------------------------- */
/* Кейсы (14 реальных проектов PromoKit, постеры — `public/cases/case_*.jpg`. */
/* Каждая запись соответствует одной картинке: id привязан к смыслу,          */
/* `image` указывает на конкретный постер, `accent` берётся из доминирующего  */
/* цвета фона на нём, `accentInk` решает, белый или тёмный текст читается на  */
/* фоне без проваливания контраста.                                           */
/* -------------------------------------------------------------------------- */

export const CASES: readonly CaseItem[] = [
  {
    id: 'epica-mi-mi-mi',
    client: 'EPICA',
    clientEn: 'EPICA',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Новогоднее путешествие',
    titleEn: 'The Big Awww Promo',
    description:
      'Зимняя чековая акция EPICA: участник регистрирует чек, получает баллы на карту лояльности торговой сети и участвует в розыгрыше путешествия.',
    descriptionEn:
      'A winter receipt-based promo for EPICA: participants upload a receipt, earn loyalty points with the retailer and enter a travel grand-prize draw.',
    image: '/cases/case_1.jpg',
    imageAlt: 'Постер кейса EPICA: лэптоп с промо-лендингом «Новогоднее путешествие»',
    imageAltEn: 'EPICA case poster: laptop showing the Big Awww Promo landing page',
    accent: '#7a0c20',
    accentInk: 'inverse',
  },
  {
    id: 'epica-ehrmann-karta-1',
    client: 'EPICA × Ehrmann',
    clientEn: 'EPICA × Ehrmann',
    eyebrow: 'Экспресс-лендинги',
    eyebrowEn: 'Express landings',
    title: 'Йогурты EPICA баллами на «Карту №1»',
    titleEn: 'EPICA yogurts as points on Karta №1',
    description:
      'Серия экспресс-лендингов для двух брендов «Лента»: чековая механика с начислением баллов на карту лояльности «Карта №1» и медийной поддержкой.',
    descriptionEn:
      'A pair of express landings for two Lenta-retail brands: receipt-based mechanics with points credited to the Karta №1 loyalty card, plus paid media support.',
    image: '/cases/case_2.jpg',
    imageAlt: 'Постер кейса EPICA × Ehrmann: лэптоп и второй лендинг с промо-механикой',
    imageAltEn: 'EPICA × Ehrmann case poster: laptop and a second landing showing the promo flow',
    accent: '#3aa6f0',
    accentInk: 'inverse',
  },
  {
    id: 'chernogolovka-cola',
    client: 'Черноголовка Cola',
    clientEn: 'Chernogolovka Cola',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Пусть радость искрится без остановки',
    titleEn: 'Let the joy keep sparkling',
    description:
      'Праздничная чековая акция Черноголовки: интерактивный лендинг с генератором предсказаний, шерингом в соцсетях и тематическим призовым фондом.',
    descriptionEn:
      'A festive receipt-based promo for Chernogolovka: an interactive landing with a fortune generator, social sharing and a holiday prize pool.',
    image: '/cases/case_3.jpg',
    imageAlt: 'Постер кейса Черноголовка Cola: лэптоп и телефон с промо-лендингом',
    imageAltEn: 'Chernogolovka Cola case poster: laptop and phone showing the promo landing',
    accent: '#b00d20',
    accentInk: 'inverse',
  },
  {
    id: 'president-routes',
    client: 'PRÉSIDENT',
    clientEn: 'PRÉSIDENT',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Постройте маршрут к удовольствиям',
    titleEn: 'Build a route to delicious moments',
    description:
      'ИИ-маршруты к удовольствиям с PRÉSIDENT: чековая механика, начисление баллов в карту «Магнит», регистрация через лендинг и Telegram-бота.',
    descriptionEn:
      'AI-curated travel routes with PRÉSIDENT: receipt-based mechanics, points credited to the Magnit loyalty card, dual sign-up via landing and a Telegram bot.',
    image: '/cases/case_4.jpg',
    imageAlt: 'Постер кейса PRÉSIDENT: лэптоп с промо-лендингом про маршруты по России',
    imageAltEn: 'PRÉSIDENT case poster: laptop showing the travel-routes promo landing',
    accent: '#e94837',
    accentInk: 'inverse',
  },
  {
    id: 'keaz-optima',
    client: 'КЭАЗ Optima',
    clientEn: 'KEAZ Optima',
    eyebrow: 'Мотивационная программа',
    eyebrowEn: 'B2B motivation programme',
    title: 'Искусство монтажа',
    titleEn: 'The art of installation',
    description:
      'Длительная B2B-программа для электромонтажников КЭАЗ Optima: чеки и коды, магазин подарков, агентские услуги и налоговое сопровождение.',
    descriptionEn:
      'A long-running B2B motivation programme for KEAZ Optima electricians: receipts and codes, a gift shop, agency services and tax handling.',
    image: '/cases/case_5.jpg',
    imageAlt: 'Постер кейса КЭАЗ Optima: лэптоп и телефон с промо-лендингом «Искусство монтажа»',
    imageAltEn: 'KEAZ Optima case poster: laptop and phone showing the Art of Installation landing',
    accent: '#e85a1a',
    accentInk: 'strong',
  },
  {
    id: 'zetter-egypt',
    client: 'Zetter × OZON',
    clientEn: 'Zetter × OZON',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Выигрывай поездку в Египет',
    titleEn: 'Win a trip to Egypt',
    description:
      'Двухлетнее чековое промо насадок Zetter в OZON: розыгрыш поездок и недельные призы, регулярные триггерные рассылки участникам.',
    descriptionEn:
      'A two-year receipt-based promo for Zetter on OZON: travel grand prize, weekly rewards and a regular trigger-based email cadence for participants.',
    image: '/cases/case_6.jpg',
    imageAlt: 'Постер кейса Zetter × OZON: лэптоп и телефон с промо-лендингом про Египет',
    imageAltEn: 'Zetter × OZON case poster: laptop and phone showing the Egypt-trip promo landing',
    accent: '#c8d629',
    accentInk: 'strong',
  },
  {
    id: 'mealberry-rio',
    client: 'Mealberry · Little One · RIO',
    clientEn: 'Mealberry · Little One · RIO',
    eyebrow: 'Мотивационная программа',
    eyebrowEn: 'Motivation programme',
    title: 'Рекомендуй, обучайся и выигрывай призы',
    titleEn: 'Recommend, learn and win prizes',
    description:
      'Обучающая программа для рекомендателей кормов Little One и RIO: кастомный личный кабинет, тестирование после каждого блока, баллы и розыгрыши OZON-сертификатов.',
    descriptionEn:
      'A learning programme for Little One and RIO recommenders: a custom dashboard, post-block tests, point accrual and OZON-voucher draws.',
    image: '/cases/case_7.jpg',
    imageAlt: 'Постер кейса Mealberry: лэптоп с программой обучения и попугай с призом',
    imageAltEn:
      'Mealberry case poster: laptop showing the learning programme alongside a parrot mascot',
    accent: '#0f8c6e',
    accentInk: 'inverse',
  },
  {
    id: 'heineken-silver-uae',
    client: 'Heineken Silver',
    clientEn: 'Heineken Silver',
    eyebrow: 'Кодовое промо в ОАЭ',
    eyebrowEn: 'Code-based promo in the UAE',
    title: 'Smooth Taste, Less Bitter',
    titleEn: 'Smooth Taste, Less Bitter',
    description:
      'Международное кодовое промо в ОАЭ для Heineken Silver: динамичный лендинг с анимацией, локализация ОС и розыгрыш Pro-наушников и вертолётного тура.',
    descriptionEn:
      'A code-on-pack promo for Heineken Silver in the UAE: a fully animated landing, multi-language onboarding and a Pro-headphones plus helicopter-tour grand prize.',
    image: '/cases/case_8.jpg',
    imageAlt: 'Постер кейса Heineken Silver: лэптоп и телефон с промо-лендингом в ОАЭ',
    imageAltEn: 'Heineken Silver case poster: laptop and phone showing the UAE promo landing',
    accent: '#7ed5d3',
    accentInk: 'strong',
  },
  {
    id: 'birra-moretti-uae',
    client: 'Birra Moretti',
    clientEn: 'Birra Moretti',
    eyebrow: 'Кодовое промо в ОАЭ',
    eyebrowEn: 'Code-based promo in the UAE',
    title: 'Would you like a slice of Italy?',
    titleEn: 'Would you like a slice of Italy?',
    description:
      'Pizza Day с Birra Moretti в ОАЭ: коды на упаковке с разделением по типам заведений, дифференцированный призовой фонд, главный приз — вилла в Тоскане.',
    descriptionEn:
      'A Pizza Day campaign for Birra Moretti in the UAE: codes split by venue type, a tiered prize pool and a Tuscany villa as the grand prize.',
    image: '/cases/case_9.jpg',
    imageAlt: 'Постер кейса Birra Moretti: лэптоп и телефон с промо-лендингом про Италию',
    imageAltEn:
      'Birra Moretti case poster: laptop and phone showing the Italy-themed promo landing',
    accent: '#dd5a1a',
    accentInk: 'strong',
  },
  {
    id: 'brewdog-uae',
    client: 'BrewDog',
    clientEn: 'BrewDog',
    eyebrow: 'Кодовое промо в ОАЭ',
    eyebrowEn: 'Code-based promo in the UAE',
    title: 'Your next favourite beer',
    titleEn: 'Your next favourite beer',
    description:
      'Международное кодовое промо BrewDog в ОАЭ: лендинг с анимацией бренда, бесшовная регистрация, главный приз — поездка в Лондон на двоих.',
    descriptionEn:
      'A code-on-pack promo for BrewDog in the UAE: a brand-animated landing, frictionless sign-up and a London trip for two as the grand prize.',
    image: '/cases/case_10.jpg',
    imageAlt: 'Постер кейса BrewDog: лэптоп и телефон с промо-лендингом «Your next favourite beer»',
    imageAltEn:
      'BrewDog case poster: laptop and phone showing the Your Next Favourite Beer landing',
    accent: '#4dbfc8',
    accentInk: 'strong',
  },
  {
    id: 'milosladovo-zolotoe-koltso',
    client: 'Милосладово',
    clientEn: 'Milosladovo',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Выиграй путешествие по Золотому кольцу на двоих',
    titleEn: 'Win a Golden Ring tour for two',
    description:
      'Чековое промо мороженого «Милосладово»: розыгрыш путешествия по Золотому кольцу, кастомизированные блоки лендинга, агентское и налоговое сопровождение.',
    descriptionEn:
      'A receipt-based promo for Milosladovo ice-cream: a Golden Ring travel grand prize, custom landing sections, agency and tax handling end-to-end.',
    image: '/cases/case_11.jpg',
    imageAlt: 'Постер кейса Милосладово: лэптоп и блок с участвующими продуктами',
    imageAltEn: 'Milosladovo case poster: laptop and a block listing the participating products',
    accent: '#5e0e2c',
    accentInk: 'inverse',
  },
  {
    id: 'jan-sobeski',
    client: 'Jan Sobeski',
    clientEn: 'Jan Sobeski',
    eyebrow: 'Сайт-визитка',
    eyebrowEn: 'Brand site',
    title: 'Декор, наполняющий пространство эстетики и смыслом',
    titleEn: 'Decor that fills space with aesthetics and meaning',
    description:
      'Премиальный многостраничный сайт-визитка декор-бренда: каталог обоев, красок, лепнины и тканей с кастомными интерактивными блоками.',
    descriptionEn:
      'A premium multi-page brand site for a decor house: catalogues of wallpapers, paints, mouldings and textiles with custom interactive sections.',
    image: '/cases/case_12.jpg',
    imageAlt: 'Постер кейса Jan Sobeski: лэптоп и каталог декора в интерьерных интерьерных сценах',
    imageAltEn: 'Jan Sobeski case poster: laptop and a decor catalogue in interior settings',
    accent: '#1a1a1a',
    accentInk: 'inverse',
  },
  {
    id: 'love-is-365',
    client: 'Love is…',
    clientEn: 'Love is…',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Акции 365/24',
    titleEn: 'Promo running 365/24',
    description:
      'Длительное смешанное промо «Love is…»: чеки и коды круглый год, магазин брендовых подарков и еженедельные розыгрыши призов.',
    descriptionEn:
      'A long-running combined promo for Love is…: receipts and codes year-round, a branded gift shop and weekly prize draws.',
    image: '/cases/case_13.jpg',
    imageAlt: 'Постер кейса Love is…: лэптоп и блок «Магазин подарков» промо-лендинга',
    imageAltEn: 'Love is… case poster: laptop and the gift-shop block of the promo landing',
    accent: '#e91e63',
    accentInk: 'inverse',
  },
  {
    id: 'lyubyatovo-dixy',
    client: 'Любятово × Дикси',
    clientEn: 'Lyubyatovo × Dixy',
    eyebrow: 'Чековое промо',
    eyebrowEn: 'Receipt-based promo',
    title: 'Приключения в ритме миллионов сердец',
    titleEn: 'Adventures in the rhythm of millions of hearts',
    description:
      'Дублирующее чековое промо Любятово в сетях «Пятёрочка» и «Дикси»: два бренд-лендинга, регистрация через сайт и Telegram-бота, общий призовой фонд.',
    descriptionEn:
      'A twin receipt-based promo for Lyubyatovo in Pyaterochka and Dixy: two brand landings, sign-up via site and a Telegram bot, a shared prize pool.',
    image: '/cases/case_14.jpg',
    imageAlt: 'Постер кейса Любятово × Дикси: лэптоп с промо-лендингом и тематические сцены',
    imageAltEn:
      'Lyubyatovo × Dixy case poster: laptop showing the promo landing with themed scenes',
    accent: '#cc1817',
    accentInk: 'inverse',
  },
];

/* -------------------------------------------------------------------------- */
/* Promo templates — «готовые шаблоны» (8 штук).                              */
/* -------------------------------------------------------------------------- */

export const PROMO_TEMPLATES: readonly PromoTemplate[] = [
  {
    id: 'receipt',
    icon: 'receipt',
    title: 'Чековая акция',
    titleEn: 'Receipt-based promo',
    description: 'Регистрация покупок по чеку с валидацией через ФНС и розыгрышем призов.',
    descriptionEn:
      'Customers register purchases by receipt; FNS validation and prize draws built in.',
    traits: [
      'Приём фото чека с распознаванием',
      'Валидация через ФНС, детекция SKU',
      'Розыгрыш призов и выдача выигрышей',
      'Личный кабинет со статусами чеков',
    ],
    traitsEn: [
      'Photo receipt upload with OCR',
      'FNS validation and SKU detection',
      'Prize draws and fulfilment flow',
      'Participant dashboard with receipt statuses',
    ],
    timeline: '3–5 недель',
    timelineEn: '3–5 weeks',
  },
  {
    id: 'code',
    icon: 'ticket',
    title: 'Кодовая акция',
    titleEn: 'Code-based promo',
    description: 'Уникальный код на упаковке, моментальный выигрыш и антифрод на входе.',
    descriptionEn: 'A unique code on each pack, instant-win redemption and anti-fraud at the door.',
    traits: [
      'Генерация до нескольких миллионов кодов',
      'Ввод с упаковки / QR',
      'Моментальный выигрыш или накопление',
      'Антифрод и rate-limit по IP и аккаунту',
    ],
    traitsEn: [
      'From a handful to millions of unique codes',
      'Pack / QR code entry',
      'Instant-win or accumulative flow',
      'Anti-fraud with per-IP and per-account rate limits',
    ],
    timeline: '2–4 недели',
    timelineEn: '2–4 weeks',
  },
  {
    id: 'game',
    icon: 'dices',
    title: 'Геймифицированная акция',
    titleEn: 'Gamified promo',
    description: 'Брендированная мини-игра с эскалацией призов и топ-листом участников.',
    descriptionEn: 'A branded mini-game with escalating prizes and a participant leaderboard.',
    traits: [
      'Колесо фортуны, 3-в-ряд, квиз',
      'Накопление очков и уровней',
      'Топ-лист участников',
      'Эскалация призов с ростом прогресса',
    ],
    traitsEn: [
      'Wheel of fortune, match-3 or quiz engines',
      'Point and level accumulation',
      'Participant leaderboard',
      'Prize escalation as progress grows',
    ],
    timeline: '4–6 недель',
    timelineEn: '4–6 weeks',
  },
  {
    id: 'messenger',
    icon: 'send',
    title: 'Акция в мессенджере',
    titleEn: 'Messenger-first promo',
    description: 'Полный цикл промо внутри Telegram / VK — без отдельного сайта.',
    descriptionEn: 'The full promo lives inside Telegram or VK — no landing required.',
    traits: [
      'Бот в Telegram / VK',
      'Приём чеков и кодов в чате',
      'Без отдельного сайта и хостинга',
      'Запуск за 1–2 недели',
    ],
    traitsEn: [
      'Bot in Telegram / VK',
      'Receipt and code intake inside the chat',
      'No landing page or separate hosting',
      'Launch within 1–2 weeks',
    ],
    timeline: '1–2 недели',
    timelineEn: '1–2 weeks',
  },
  {
    id: 'co-brand',
    icon: 'share-2',
    title: 'Партнёрская акция',
    titleEn: 'Co-branded promo',
    description: 'Два и более бренда в одной механике с общим призовым фондом.',
    descriptionEn: 'Two or more brands sharing one mechanic and a joint prize pool.',
    traits: [
      '2+ бренда в одной механике',
      'Общий призовой фонд',
      'Сквозная аналитика по всем брендам',
      'Единый регламент и модерация',
    ],
    traitsEn: [
      '2+ brands under one mechanic',
      'Shared prize pool',
      'Cross-brand analytics in one dashboard',
      'A single ruleset and moderation flow',
    ],
    timeline: '4–6 недель',
    timelineEn: '4–6 weeks',
  },
  {
    id: 'ugc',
    icon: 'megaphone',
    title: 'UGC-акция',
    titleEn: 'UGC promo',
    description: 'Пользователи присылают фото и видео, бренд получает контент и вовлечённость.',
    descriptionEn: 'Users submit photos and videos; the brand collects content and engagement.',
    traits: [
      'Публикация фото / видео с хэштегом',
      'Автоматический сбор из соцсетей',
      'Модерация и защита от токсичности',
      'Пользовательское голосование',
    ],
    traitsEn: [
      'Photo and video posts with a hashtag',
      'Automatic ingestion from social networks',
      'Moderation and toxicity filters',
      'On-site user voting',
    ],
    timeline: '3–5 недель',
    timelineEn: '3–5 weeks',
  },
  {
    id: 'loyalty',
    icon: 'trophy',
    title: 'Клубная программа',
    titleEn: 'Loyalty club',
    description: 'Долгосрочная программа лояльности с уровнями и персональными бонусами.',
    descriptionEn: 'A long-running loyalty programme with tiers and personalised rewards.',
    traits: [
      'Уровни членства (silver / gold / platinum)',
      'Накопительные бонусы и баллы',
      'Персональные предложения',
      'Долгосрочное удержание аудитории',
    ],
    traitsEn: [
      'Membership tiers (silver / gold / platinum)',
      'Accumulative bonuses and points',
      'Personalised offers per member',
      'Long-term audience retention',
    ],
    timeline: '6–8 недель',
    timelineEn: '6–8 weeks',
  },
];

/* -------------------------------------------------------------------------- */
/* Pricing teaser (content/08-pricing-calculator.md)                          */
/* -------------------------------------------------------------------------- */

export const PRICING_BENEFITS: readonly string[] = [
  'Готовая смета в Excel',
  'Индивидуальное КП',
  'TimePlan',
  'Книга кейсов',
];

export const PRICING_BENEFITS_EN: readonly string[] = [
  'A locked estimate in Excel',
  'A tailored proposal',
  'TimePlan',
  'The case book',
];
